import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShoppingCart, Download, Check, X, Loader2, Printer } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

import { API } from '@/lib/apiBase';

export const PhotoPreviewDialog = ({
  photo, onClose,
  isInCart, canDownloadFree, downloading,
  onToggleCart, onDownload,
  printFormats, selectedFormat, setSelectedFormat, onAddPrint
}) => {
  const { t } = useLanguage();
  const free = photo ? canDownloadFree(photo) : false;
  const inCart = photo ? isInCart(photo.photo_id) : false;

  return (
    <Dialog open={!!photo} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-card border-border p-0 overflow-hidden">
        <DialogTitle className="sr-only">{t('gallery.photoPreview')}</DialogTitle>
        {photo && (
          <>
            <div className="relative">
              <img
                src={`${API}/photos/${photo.photo_id}/preview`}
                alt={t('gallery.photoPreview')}
                className="w-full max-h-[60vh] object-contain bg-black"
              />
              <Button
                variant="ghost" size="icon"
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70"
                onClick={onClose}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className={`font-mono text-2xl font-bold mb-1 ${free ? 'text-green-500' : 'text-primary'}`}>
                    {free ? t('gallery.free') : `CHF ${photo.price?.toFixed(2)}`}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {photo.is_class_photo ? t('gallery.classPhoto') : t('gallery.individualPhotos')}
                    {' · '}{t('prints.digitalDownload')}
                  </p>
                </div>

                <div className="flex gap-3">
                  {free ? (
                    <Button
                      onClick={() => onDownload(photo)}
                      disabled={downloading === photo.photo_id}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {downloading === photo.photo_id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {t('gallery.downloadFree')}
                    </Button>
                  ) : (
                    <Button
                      variant={inCart ? 'secondary' : 'default'}
                      onClick={() => onToggleCart(photo)}
                      className={inCart ? '' : 'bg-primary hover:bg-primary/90'}
                      data-testid="dialog-add-cart"
                    >
                      {inCart ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          {t('gallery.inCart')}
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          {t('gallery.addToCart')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Prints are always shown at the standard print_format price,
                  even when the digital download is free (teachers) — the
                  free/paid distinction only applies to the digital copy. */}
              {printFormats.length > 0 && (
                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Printer className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold text-sm">{t('prints.orderPrint')}</h4>
                  </div>
                  <div className="flex gap-3">
                    <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                      <SelectTrigger className="flex-1 bg-secondary border-border" data-testid="print-format-select">
                        <SelectValue placeholder={t('prints.selectFormat')} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {printFormats.map((fmt) => (
                          <SelectItem key={fmt.format_id} value={fmt.format_id}>
                            {fmt.name} — CHF {fmt.price?.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => onAddPrint(photo)}
                      disabled={!selectedFormat}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="add-print-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      {t('prints.addPrint')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
